import { useState, useCallback } from 'react';
import { logger } from '@core/utils';
import { useGetFAQsQuery, useDeleteFAQMutation } from '../api/faqApi';

/**
 * Custom hook for FAQ operations (list, delete)
 * Encapsulates all business logic for FAQ management
 *
 * @param {Object} params
 * @param {Function} params.t - Translation function from react-i18next
 * @param {string} [params.userId] - Filter FAQs by user ID
 * @param {string} [params.segmentedKnowledgeId] - Filter FAQs by segment ID
 *
 * @returns {Object} FAQ operations and state
 *
 * @example
 * const { faqs, isLoading, deleteFAQ, error, successMessage } = useFAQOperations({ t, userId, segmentedKnowledgeId });
 */
export function useFAQOperations({ t, userId = null, segmentedKnowledgeId = null }) {
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // RTK Query hooks with optional filters
  const queryParams = {};
  if (userId) queryParams.userId = userId;
  if (segmentedKnowledgeId) queryParams.segmentedKnowledgeId = segmentedKnowledgeId;

  const { data: faqs = [], isLoading, refetch } = useGetFAQsQuery(queryParams);
  const [deleteFAQMutation] = useDeleteFAQMutation();

  /**
   * Delete a FAQ entry
   * @param {string} faqId - ID of FAQ to delete
   */
  const deleteFAQ = useCallback(async (faqId) => {
    try {
      await deleteFAQMutation(faqId).unwrap();
      setSuccessMessage(t('faq.deleteSuccess', 'FAQ deleted successfully'));
      // FAQ list will automatically refresh via RTK Query cache invalidation
    } catch (err) {
      logger.error('Delete error:', err);
      setError(t('faq.deleteError', 'Failed to delete FAQ'));
    }
  }, [deleteFAQMutation, t]);

  /**
   * Clear error message
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Clear success message
   */
  const clearSuccess = useCallback(() => {
    setSuccessMessage(null);
  }, []);

  return {
    faqs,
    isLoading,
    deleteFAQ,
    error,
    successMessage,
    clearError,
    clearSuccess,
    refetch,
  };
}
